import * as THREE from 'three';
import { ATMO, EFFECTS, PARTICLE_FADE } from '../config/tuning';
import { hashUnit } from '../core/Rng';
import { createSoftPoints, type SoftPoints } from './SoftPoints';

/**
 * One pooled particle system behind every effect in SPEC §5.3 that is not a ribbon.
 *
 * Explosions, secondary fires, debris, water plumes and damage smoke are all the same thing —
 * a point with a velocity, a lifetime, a colour ramp and a size ramp — so they share one buffer,
 * one draw call and one hard ceiling. That ceiling is the reason it is built this way: the M7
 * budget is 2 ms at peak, and the way particle systems blow a budget is by being several systems
 * that each individually seemed reasonable.
 *
 * Emission is deterministic. Spread comes from hashing the particle's index rather than from a
 * stream, so a replay produces the same explosion (SPEC §4.3) without the emitters having to
 * agree on an order.
 *
 * Units: metres, m/s, seconds.
 */

export const ParticleKind = {
  Smoke: 0,
  Fire: 1,
  Debris: 2,
  Plume: 3,
} as const;
export type ParticleKindId = (typeof ParticleKind)[keyof typeof ParticleKind];

const spawnScratch = new THREE.Vector3();

/**
 * Edge softness per kind, 0 hard to 1 very soft.
 *
 * Debris is nearly hard because it is a solid fragment and should read as one; fire and smoke are
 * soft so a cluster of sprites blends into volume instead of a pile of discs. Plumes sit between:
 * water throws up droplets with a visible edge.
 */
const SOFTNESS: Record<number, number> = {
  0: 0.95, // Smoke
  1: 0.8, // Fire
  2: 0.25, // Debris
  3: 0.65, // Plume
};

export class Particles {
  readonly object3d: THREE.Points;

  private readonly positions: Float32Array;
  private readonly colours: Float32Array;
  private readonly sizes: Float32Array;
  private readonly geometry: THREE.BufferGeometry;
  private readonly soft: SoftPoints;
  private readonly material: THREE.ShaderMaterial;
  /** Per-particle (life alpha, edge softness) — see the particle shader. */
  private readonly params: Float32Array;

  // Simulation state, parallel arrays: the layout a particle system wants.
  private readonly velocityX: Float32Array;
  private readonly velocityY: Float32Array;
  private readonly velocityZ: Float32Array;
  private readonly ageS: Float32Array;
  private readonly lifetimeS: Float32Array;
  private readonly baseSize: Float32Array;
  private readonly kind: Uint8Array;

  private count = 0;
  private spawnCounter = 0;

  constructor() {
    const max = EFFECTS.maxParticles;
    this.positions = new Float32Array(max * 3);
    this.colours = new Float32Array(max * 3);
    this.sizes = new Float32Array(max);
    this.params = new Float32Array(max * 2);
    this.velocityX = new Float32Array(max);
    this.velocityY = new Float32Array(max);
    this.velocityZ = new Float32Array(max);
    this.ageS = new Float32Array(max);
    this.lifetimeS = new Float32Array(max);
    this.baseSize = new Float32Array(max);
    this.kind = new Uint8Array(max);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colours, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(this.sizes, 1));
    this.geometry.setAttribute('aParams', new THREE.BufferAttribute(this.params, 2));

    // Additive so overlapping fire compounds into a bright core, which is most of what makes a
    // cluster of sprites read as one fireball rather than as separate discs.
    this.soft = createSoftPoints({ additive: true });
    this.material = this.soft.material;

    this.object3d = new THREE.Points(this.geometry, this.material);
    this.object3d.frustumCulled = false;
    this.object3d.renderOrder = 11;
  }

  get liveCount(): number {
    return this.count;
  }

  clear(): void {
    this.count = 0;
    this.geometry.setDrawRange(0, 0);
  }

  dispose(): void {
    this.geometry.dispose();
    this.soft.dispose();
  }

  /**
   * Emit one particle. Silently drops it when the pool is full — a dropped particle is invisible,
   * whereas growing the buffer mid-explosion is a frame-time spike the player can feel.
   */
  emit(
    positionM: THREE.Vector3,
    velocityMs: THREE.Vector3,
    lifetimeS: number,
    sizeM: number,
    colour: THREE.Color,
    kind: ParticleKindId,
  ): void {
    if (this.count >= EFFECTS.maxParticles) return;
    const i = this.count;
    this.count += 1;

    this.positions[i * 3] = positionM.x;
    this.positions[i * 3 + 1] = positionM.y;
    this.positions[i * 3 + 2] = positionM.z;
    this.velocityX[i] = velocityMs.x;
    this.velocityY[i] = velocityMs.y;
    this.velocityZ[i] = velocityMs.z;
    this.ageS[i] = 0;
    this.lifetimeS[i] = lifetimeS;
    this.baseSize[i] = sizeM;
    this.kind[i] = kind;
    this.colours[i * 3] = colour.r;
    this.colours[i * 3 + 1] = colour.g;
    this.colours[i * 3 + 2] = colour.b;
    this.sizes[i] = sizeM;
    this.params[i * 2] = 1;
    this.params[i * 2 + 1] = SOFTNESS[kind];
  }

  /**
   * Emit a burst in a cone or sphere. `spread` of 1 is a full sphere, 0 a straight line.
   *
   * The direction of each particle comes from hashing a counter rather than from a PRNG stream,
   * so two explosions that happen on the same tick do not depend on which was emitted first.
   */
  burst(
    positionM: THREE.Vector3,
    direction: THREE.Vector3,
    speedMs: number,
    count: number,
    spread: number,
    lifetimeS: number,
    sizeM: number,
    colour: THREE.Color,
    kind: ParticleKindId,
  ): void {
    for (let i = 0; i < count; i += 1) {
      const seed = this.spawnCounter;
      this.spawnCounter += 1;

      // Uniform-ish direction on a sphere, biased toward `direction` by `spread`.
      const theta = hashUnit(seed, 0x11) * Math.PI * 2;
      const z = hashUnit(seed, 0x22) * 2 - 1;
      const r = Math.sqrt(Math.max(0, 1 - z * z));
      spawnScratch.set(Math.cos(theta) * r, z, Math.sin(theta) * r);
      spawnScratch.lerp(direction, 1 - spread).normalize();

      const speed = speedMs * (0.55 + 0.45 * hashUnit(seed, 0x33));
      spawnScratch.multiplyScalar(speed);

      const life = lifetimeS * (0.7 + 0.6 * hashUnit(seed, 0x44));
      const size = sizeM * (0.65 + 0.7 * hashUnit(seed, 0x55));
      this.emit(positionM, spawnScratch, life, size, colour, kind);
    }
  }

  /**
   * Advance every particle and retire the dead. Swap-removal, so the live set stays packed and
   * the draw range is always `[0, count)`.
   */
  update(dtS: number): void {
    for (let i = this.count - 1; i >= 0; i -= 1) {
      this.ageS[i] += dtS;
      const life = this.lifetimeS[i];
      if (this.ageS[i] >= life) {
        this.swapRemove(i);
        continue;
      }

      const kind = this.kind[i];

      // Debris falls and tumbles; smoke and fire rise and slow; plumes arc back down.
      if (kind === ParticleKind.Debris || kind === ParticleKind.Plume) {
        this.velocityY[i] -= ATMO.G * dtS;
      } else {
        const rise = kind === ParticleKind.Fire ? EFFECTS.fireRiseMs : EFFECTS.smokeRiseMs;
        this.velocityY[i] += (rise - this.velocityY[i]) * Math.min(1, dtS * 1.5);
      }

      const drag = Math.exp(-(kind === ParticleKind.Debris ? 0.5 : 1.4) * dtS);
      this.velocityX[i] *= drag;
      this.velocityZ[i] *= drag;

      this.positions[i * 3] += this.velocityX[i] * dtS;
      this.positions[i * 3 + 1] += this.velocityY[i] * dtS;
      this.positions[i * 3 + 2] += this.velocityZ[i] * dtS;

      // Smoke and fire expand as they age; debris does not.
      const t = this.ageS[i] / life;
      const growth = kind === ParticleKind.Debris ? 1 : 1 + t * 2.2;
      this.sizes[i] = this.baseSize[i] * growth;

      // Fade over life. A particle that disappears at full opacity pops; one that thins out
      // reads as smoke dispersing. Debris holds its opacity and simply vanishes at range, which
      // is how a solid fragment behaves.
      this.params[i * 2] =
        kind === ParticleKind.Debris ? 1 : Math.min(1, (1 - t) * PARTICLE_FADE.sharpness);

      // Fire cools toward smoke over its life, which is what makes a fire column read as one.
      if (kind === ParticleKind.Fire) {
        const fade = 1 - t;
        this.colours[i * 3] *= 0.985 + 0.015 * fade;
        this.colours[i * 3 + 1] *= 0.97 + 0.02 * fade;
        this.colours[i * 3 + 2] *= 0.96 + 0.02 * fade;
      }
    }

    this.geometry.attributes['position'].needsUpdate = true;
    this.geometry.attributes['color'].needsUpdate = true;
    this.geometry.attributes['size'].needsUpdate = true;
    this.geometry.attributes['aParams'].needsUpdate = true;
    this.geometry.setDrawRange(0, this.count);
  }

  private swapRemove(index: number): void {
    const last = this.count - 1;
    if (index !== last) {
      for (let axis = 0; axis < 3; axis += 1) {
        this.positions[index * 3 + axis] = this.positions[last * 3 + axis];
        this.colours[index * 3 + axis] = this.colours[last * 3 + axis];
      }
      this.sizes[index] = this.sizes[last];
      this.params[index * 2] = this.params[last * 2];
      this.params[index * 2 + 1] = this.params[last * 2 + 1];
      this.velocityX[index] = this.velocityX[last];
      this.velocityY[index] = this.velocityY[last];
      this.velocityZ[index] = this.velocityZ[last];
      this.ageS[index] = this.ageS[last];
      this.lifetimeS[index] = this.lifetimeS[last];
      this.baseSize[index] = this.baseSize[last];
      this.kind[index] = this.kind[last];
    }
    this.count -= 1;
  }
}
